import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { getSupabase, signIn as lazySignIn, signOut as lazySignOut, getSession, onAuthStateChange } from '../lib/supabase-lazy'

interface UserProfile {
  id: string
  organization_id: string
  role: 'super_admin' | 'pcm_staff' | 'client_admin' | 'client_user'
  full_name: string | null
  avatar_url: string | null
  phone: string | null
  metadata: Record<string, any>
}

interface PlatformUser {
  id: string
  email: string
  full_name: string
  avatar_url: string | null
  platform_account_id: string
  role: 'owner' | 'admin' | 'member'
  auth_user_id: string
  is_active: boolean
  last_login_at: string | null
  notification_preferences: Record<string, any>
}

interface Organization {
  id: string
  name: string
  slug: string
  logo_url: string | null
  settings: Record<string, any>
  subscription_tier: string
}

interface AuthContextType {
  user: User | null
  userProfile: UserProfile | null
  organization: Organization | null
  session: Session | null
  loading: boolean
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ error: Error | null; requiresMFA?: boolean; session?: any }>
  signUp: (email: string, password: string, organizationId?: string, role?: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<{ error: Error | null }>
  resetPassword: (email: string, redirectTo?: string) => Promise<{ error: Error | null }>
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>
  hasRole: (roles: string[]) => boolean
  canAccess: (resource: string, action: string) => boolean
  clearExpiredSession: () => void
  // MFA methods
  enrollMFA: () => Promise<{ error: Error | null; qrCode?: string; secret?: string }>
  verifyMFA: (token: string) => Promise<{ error: Error | null }>
  unenrollMFA: () => Promise<{ error: Error | null }>
  getMFAStatus: () => Promise<{ enrolled: boolean; verified: boolean }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    initializeAuth()
  }, [])

  const initializeAuth = async () => {
    try {
      // Check active session
      const { data: { session } } = await getSession()
      
      // Check if MFA has been verified for this session
      const mfaVerified = sessionStorage.getItem('mfa_verified') === 'true'
      const mfaBypassTrusted = sessionStorage.getItem('mfa_bypass_trusted') === 'true'
      
      // Allow certain paths without MFA verification
      const currentPath = window.location.pathname
      const mfaExemptPaths = ['/mfa-setup', '/mfa-verify', '/login', '/client-portal/login']
      const isExemptPath = mfaExemptPaths.some(path => currentPath.startsWith(path))
      
      if (session?.user && !mfaVerified && !isExemptPath) {
        // If we have a bypass flag from trusted device, treat as MFA verified
        if (mfaBypassTrusted) {
          sessionStorage.removeItem('mfa_bypass_trusted') // Clean up the flag
          setSession(session)
          setUser(session.user)
          try {
            await fetchUserProfile(session.user.id)
          } catch (error: any) {
            // Failed to fetch user profile
          }
        } else {
          // User has session but hasn't completed MFA and is not on an exempt page
          // Keep the session but mark user as needing MFA
          setSession(session)
          setUser(session.user)
          // Don't fetch profile yet - they need to complete MFA first
          setLoading(false)
          return
        }
      }
      
      // Only set user if MFA is verified or no session exists
      setSession(session)
      setUser(session?.user ?? null)
      
      if (session?.user && mfaVerified) {
        // User found in session and MFA verified
        try {
          await fetchUserProfile(session.user.id)
        } catch (error: any) {
          // Failed to fetch user profile - will handle in catch block
          // If JWT is expired, the fetchUserProfile will handle signing out
        }
      } else {
        // No user in session
        setLoading(false)
      }

      // Listen for auth changes
      const authStateResult = await onAuthStateChange((_event: any, session: any) => {
        // Auth state changed
        const mfaVerified = sessionStorage.getItem('mfa_verified') === 'true'
        const currentPath = window.location.pathname
        const mfaExemptPaths = ['/mfa-setup', '/mfa-verify', '/login', '/client-portal/login']
        const isExemptPath = mfaExemptPaths.some(path => currentPath.startsWith(path))
        
        // Handle session changes
        setSession(session)
        setUser(session?.user ?? null)
        
        if (session?.user && mfaVerified) {
          fetchUserProfile(session.user.id)
        } else if (session?.user && !mfaVerified && isExemptPath) {
          // User is on MFA setup page, allow but don't fetch full profile
          setUserProfile(null)
          setOrganization(null)
        } else if (session?.user && !mfaVerified) {
          // User needs MFA but is trying to access protected page
          setUserProfile(null)
          setOrganization(null)
          // They'll be redirected by ProtectedRoute
        } else {
          setUserProfile(null)
          setOrganization(null)
        }
      })
      
      const subscription = authStateResult.data?.subscription

      // Store unsubscribe function for cleanup
      if (subscription?.unsubscribe) {
        (window as any).__authUnsubscribe = subscription.unsubscribe
      }
    } catch (error) {
      // Failed to initialize auth
      setLoading(false)
    }
  }

  useEffect(() => {
    return () => {
      // Cleanup subscription
      if ((window as any).__authUnsubscribe) {
        (window as any).__authUnsubscribe()
      }
    }
  }, [])

  const fetchUserProfile = async (userId: string) => {
    // Fetching user profile
    try {
      const supabase = await getSupabase()
      if (!supabase) {
        throw new Error('Supabase not available')
      }

      // Try to fetch from platform_users first (new model)
      // Checking platform_users table
      let platformUser = null
      let platformError = null
      
      try {
        const result = await supabase
          .from('platform_users')
          .select('*')
          .eq('auth_user_id', userId)
          .single()
        platformUser = result.data
        platformError = result.error
      } catch (e) {
        // Platform users query failed
        platformError = e
      }

      if (!platformError && platformUser) {
        // Platform user found
        // Map platform user to legacy UserProfile format for backward compatibility
        const mappedProfile: UserProfile = {
          id: platformUser.id,
          organization_id: platformUser.platform_account_id,
          role: platformUser.role === 'owner' ? 'super_admin' : platformUser.role === 'admin' ? 'pcm_staff' : 'pcm_staff',
          full_name: platformUser.full_name,
          avatar_url: platformUser.avatar_url,
          phone: null,
          metadata: platformUser.notification_preferences || {}
        }
        setUserProfile(mappedProfile)
        // User profile set from platform_users

        // Fetch platform account details
        if (platformUser.platform_account_id) {
          // Fetching platform account
          const { data: account, error: accountError } = await supabase
            .from('platform_accounts')
            .select('*')
            .eq('id', platformUser.platform_account_id)
            .single()

          if (!accountError && account) {
            // Map to Organization for backward compatibility
            const mappedOrg: Organization = {
              id: account.id,
              name: account.company_name,
              slug: account.company_name.toLowerCase().replace(/\s+/g, '-'),
              logo_url: null,
              settings: account.settings || {},
              subscription_tier: account.subscription_tier
            }
            setOrganization(mappedOrg)
            // Organization set from platform_account
          }
        }
      } else {
        // Check if this is a client user
        const { data: clientUser, error: clientError } = await supabase
          .from('client_users')
          .select(`
            *,
            party:parties(*)
          `)
          .eq('auth_user_id', userId)
          .single()

        if (!clientError && clientUser) {
          // Client user found
          // Map client user to UserProfile format
          const mappedProfile: UserProfile = {
            id: clientUser.id,
            organization_id: clientUser.party?.id || '',
            role: 'client_user',
            full_name: clientUser.party?.name || clientUser.email,
            avatar_url: null,
            phone: clientUser.party?.phone || null,
            metadata: {}
          }
          setUserProfile(mappedProfile)
          
          // For client users, set a simple organization object
          if (clientUser.party) {
            const mappedOrg: Organization = {
              id: clientUser.party.id,
              name: clientUser.party.name,
              slug: clientUser.party.name.toLowerCase().replace(/\s+/g, '-'),
              logo_url: null,
              settings: {},
              subscription_tier: 'client'
            }
            setOrganization(mappedOrg)
          }
        } else {
          // Fallback to legacy users table
          // Platform user not found, checking legacy users table
          const { data: profile, error: profileError } = await supabase
            .from('users')
            .select('*')
            .eq('id', userId)
            .single()

          // User profile query completed

          if (profileError) {
            // Profile fetch error - user may not exist in legacy table
            throw profileError
          }

          setUserProfile(profile)
          // User profile set

          // Fetch organization
          if (profile.organization_id) {
            const { data: org, error: orgError } = await supabase
              .from('organizations')
              .select('*')
              .eq('id', profile.organization_id)
              .single()

            // Organization query completed

            if (orgError) {
              // Organization fetch error - may not have an organization
              throw orgError
            }
            setOrganization(org)
            // Organization set
          }
        }
      }
    } catch (error: any) {
      // Error fetching user profile
      
      // Check if it's a JWT expired error
      if (error?.message?.includes('JWT expired') || error?.message?.includes('PGRST301')) {
        // JWT expired, signing out
        await signOut();
      }
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      // Attempting sign in
      
      // Store remember me preference
      window.localStorage.setItem('supabase.auth.rememberMe', rememberMe.toString())
      
      const { data, error } = await lazySignIn(email, password)
      
      if (error) {
        // Sign in error - will return error object
        return { error }
      }

      // Sign in successful
      
      // Mark session as temporary if not remembering
      if (!rememberMe) {
        window.sessionStorage.setItem('supabase.auth.temp-session', 'true')
      }
      
      // TOTP MFA is now legacy - passkeys provide stronger security
      // Users can set up passkeys in Security Settings for enhanced protection
      // No longer force TOTP MFA during login
      const requiresMFA = false
      sessionStorage.setItem('mfa_verified', 'true')

      return { error: null, requiresMFA, session: data?.session }
    } catch (error) {
      // Sign in exception
      return { error: error as Error }
    }
  }

  const signUp = async (email: string, password: string, organizationId?: string, role: string = 'client_user') => {
    try {
      const supabase = await getSupabase()
      if (!supabase) {
        return { error: new Error('Supabase not available') }
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            organization_id: organizationId,
            role
          }
        }
      })

      if (error) return { error }

      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  const clearExpiredSession = () => {
    // Clearing expired session
    // Clear all auth-related items from both storages
    const authKeys = [
      'supabase-auth-token',
      'supabase-refresh-token', 
      'supabase-user',
      'supabase-auth-timestamp',
      'supabase.auth.rememberMe',
      'supabase.auth.temp-session'
    ]
    
    authKeys.forEach(key => {
      localStorage.removeItem(key)
      sessionStorage.removeItem(key)
    })
    
    // Also clear any keys that start with 'sb-' or 'supabase'
    const clearStorageKeys = (storage: Storage) => {
      const keysToRemove: string[] = []
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i)
        if (key && (key.startsWith('sb-') || key.startsWith('supabase'))) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(key => storage.removeItem(key))
    }
    
    clearStorageKeys(localStorage)
    clearStorageKeys(sessionStorage)
    
    // Reset state
    setUser(null)
    setUserProfile(null)
    setOrganization(null)
    setSession(null)
  }

  const signOut = async () => {
    try {
      const { error } = await lazySignOut()
      if (error) return { error }
      
      // Clear all session data
      clearExpiredSession()
      
      // Clear MFA verification
      sessionStorage.removeItem('mfa_verified')
      
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  const resetPassword = async (email: string, redirectTo?: string) => {
    try {
      const supabase = await getSupabase()
      if (!supabase) {
        return { error: new Error('Supabase not available') }
      }

      // Determine the base URL based on environment
      const baseUrl = import.meta.env.PROD 
        ? 'https://pcmfinancial.ca'
        : 'http://localhost:5173'
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectTo || `${baseUrl}/reset-password`
      })

      if (error) return { error }
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  const updatePassword = async (newPassword: string) => {
    try {
      const supabase = await getSupabase()
      if (!supabase) {
        return { error: new Error('Supabase not available') }
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (error) return { error }
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  const hasRole = (roles: string[]) => {
    if (!userProfile) return false
    return roles.includes(userProfile.role)
  }

  const canAccess = (resource: string, action: string) => {
    if (!userProfile) return false
    
    // Super admin can access everything
    if (userProfile.role === 'super_admin') return true
    
    // Define permissions based on roles
    const permissions: Record<string, Record<string, string[]>> = {
      pcm_staff: {
        users: ['read', 'create', 'update'],
        organizations: ['read', 'update'],
        billing: ['read'],
      },
      client_admin: {
        users: ['read', 'create', 'update'],
        billing: ['read'],
      },
      client_user: {
        users: ['read'],
      }
    }
    
    const rolePermissions = permissions[userProfile.role]
    if (!rolePermissions) return false
    
    const resourcePermissions = rolePermissions[resource]
    if (!resourcePermissions) return false
    
    return resourcePermissions.includes(action)
  }

  // MFA methods
  const enrollMFA = async () => {
    try {
      const supabase = await getSupabase()
      if (!supabase) {
        return { error: new Error('Supabase not available') }
      }

      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'PCM Portal'
      })

      if (error) return { error }

      // Generate QR code
      const qrCode = data?.totp?.qr_code
      const secret = data?.totp?.secret

      return { error: null, qrCode, secret }
    } catch (error) {
      return { error: error as Error }
    }
  }

  const verifyMFA = async (token: string) => {
    try {
      const supabase = await getSupabase()
      if (!supabase) {
        return { error: new Error('Supabase not available') }
      }

      const { data: factors } = await supabase.auth.mfa.listFactors()
      const totpFactor = factors?.totp?.[0]

      if (!totpFactor) {
        return { error: new Error('No TOTP factor found') }
      }

      const { error } = await supabase.auth.mfa.verify({
        factorId: totpFactor.id,
        code: token
      })

      if (error) return { error }
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  const unenrollMFA = async () => {
    try {
      const supabase = await getSupabase()
      if (!supabase) {
        return { error: new Error('Supabase not available') }
      }

      const { data: factors } = await supabase.auth.mfa.listFactors()
      const totpFactor = factors?.totp?.[0]

      if (!totpFactor) {
        return { error: new Error('No TOTP factor found') }
      }

      const { error } = await supabase.auth.mfa.unenroll({
        factorId: totpFactor.id
      })

      if (error) return { error }
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  const getMFAStatus = async () => {
    try {
      const supabase = await getSupabase()
      if (!supabase) {
        return { enrolled: false, verified: false }
      }

      const { data: factors } = await supabase.auth.mfa.listFactors()
      const totpFactor = factors?.totp?.[0]

      return {
        enrolled: !!totpFactor,
        verified: totpFactor?.status === 'verified'
      }
    } catch (error) {
      // Error getting MFA status
      return { enrolled: false, verified: false }
    }
  }

  const value = {
    user,
    userProfile,
    organization,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    hasRole,
    canAccess,
    enrollMFA,
    verifyMFA,
    unenrollMFA,
    getMFAStatus,
    clearExpiredSession
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}